import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Select,
  InputNumber,
  Drawer,
  Divider,
  DatePicker,
  Typography,
  Rate,
  Badge,
  Alert
} from 'antd';
import {
  CarOutlined,
  PlusOutlined,
  SettingOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  StarOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { deliveryRatingsAPI, supplierDeliveryTeamAPI, API_BASE_URL } from '../services/api';
import AdminApprovals from './AdminApprovals';
import { useIsMobile } from '../hooks/useIsMobile';

const { Text } = Typography;

const API_ORIGIN = API_BASE_URL;

interface DeliveryRoute {
  id: number;
  fromAddress: string;
  toAddress: string;
  distance: number;
  estimatedTime: number;
  autoRate: number;
  customRate: number;
}

interface DriverDelivery {
  orderId: number;
  deliveredAt: string | Date;
  customerCompany?: string | null;
  supplierCompany?: string | null;
  existingRating?: {
    rating: number;
    comment?: string | null;
  } | null;
}

interface DeliveryDriver {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  phone: string;
  defaultRate: number;
  totalDeliveries: number;
  avgRating: number;
  ratingCount: number;
  totalEarnings: number;
  status: string;
  routes: DeliveryRoute[];
  deliveries: DriverDelivery[];
}

interface SalaryResult {
  periodLabel: string;
  basePay: number;
  routePay: number;
  bonus: number;
  penalty: number;
  totalDeliveries: number;
  finalPay: number;
}

const MyDeliveryTeam: React.FC = () => {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const [loading, setLoading] = React.useState(false);
  const [drivers, setDrivers] = React.useState<DeliveryDriver[]>([]);
  const [addModalVisible, setAddModalVisible] = React.useState(false);
  const [routesDrawerVisible, setRoutesDrawerVisible] = React.useState(false);
  const [selectedDriver, setSelectedDriver] = React.useState<DeliveryDriver | null>(null);
  const [addRouteVisible, setAddRouteVisible] = React.useState(false);
  const [routeForm] = Form.useForm();
  const [salaryModalVisible, setSalaryModalVisible] = React.useState(false);
  const [salaryDriver, setSalaryDriver] = React.useState<DeliveryDriver | null>(null);
  const [salaryResult, setSalaryResult] = React.useState<SalaryResult | null>(null);
  const [salaryLoading, setSalaryLoading] = React.useState(false);
  const [salaryForm] = Form.useForm();
  const [rateModalVisible, setRateModalVisible] = React.useState(false);
  const [rateDriverState, setRateDriverState] = React.useState<DeliveryDriver | null>(null);
  const [rateLoading, setRateLoading] = React.useState(false);
  const [rateForm] = Form.useForm();
  const [registrationsDrawerOpen, setRegistrationsDrawerOpen] = React.useState(false);
  const [pendingRegistrationsCount, setPendingRegistrationsCount] = React.useState(0);
  const [editDriverVisible, setEditDriverVisible] = React.useState(false);
  const [editDriverSaving, setEditDriverSaving] = React.useState(false);
  const [editingDriver, setEditingDriver] = React.useState<DeliveryDriver | null>(null);
  const [editDriverForm] = Form.useForm();

  const canReviewRegistrations =
    user?.role === 'customer' || user?.role === 'supplier';

  const loadPendingRegistrationsCount = React.useCallback(async () => {
    if (!canReviewRegistrations || !user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_ORIGIN}/api/auth/approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const list = await res.json();
        setPendingRegistrationsCount(list.filter((a: { status: string }) => a.status === 'pending').length);
      }
    } catch {
      /* ignore */
    }
  }, [canReviewRegistrations, user]);

  React.useEffect(() => {
    loadPendingRegistrationsCount();
    const t = setInterval(loadPendingRegistrationsCount, 60000);
    return () => clearInterval(t);
  }, [loadPendingRegistrationsCount]);

  const loadLocalDrivers = React.useCallback(() => {
    const stored = localStorage.getItem('my_delivery_drivers');
    if (stored) {
      try {
        const parsed: DeliveryDriver[] = JSON.parse(stored);
        // Clean legacy test driver (could be left from old builds)
        const filtered = parsed.filter((d) => {
          const email = String(d?.userEmail || '').toLowerCase();
          const name = String(d?.userName || '').toLowerCase();
          const uname = String((d as any)?.username || '').toLowerCase();
          if (email === 'delivery@example.com') return false;
          if (uname === 'delivery') return false;
          if (name === 'delivery' || name.includes('test')) return false;
          return true;
        });
        const normalized = filtered.map((driver) => ({
          ...driver,
          ratingCount: driver.ratingCount ?? 0,
          deliveries: driver.deliveries ?? [],
          routes: driver.routes ?? []
        }));
        setDrivers(normalized);
        if (filtered.length !== parsed.length) {
          localStorage.setItem('my_delivery_drivers', JSON.stringify(normalized));
        }
        return normalized;
      } catch (error) {
        console.error('Не удалось прочитать локальных доставщиков', error);
      }
    }
    setDrivers([]);
    return [];
  }, []);

  const loadDriversFromApi = React.useCallback(async () => {
    setLoading(true);
    try {
      const scope: 'supplier' | 'customer' | 'admin' | undefined =
        user?.role === 'customer' ? 'customer' : user?.role === 'supplier' ? 'supplier' : undefined;

      const storedDrivers = loadLocalDrivers();
      const response = await deliveryRatingsAPI.getDrivers(scope);
      const apiDrivers = Array.isArray(response?.drivers) ? response.drivers : [];

      if (apiDrivers.length === 0) {
        return;
      }

      const mergedDrivers: DeliveryDriver[] = apiDrivers.map((driver: any) => {
        const stored = storedDrivers.find((d) => d.userId === driver.driverId);
        return {
          id: driver.driverId,
          userId: driver.driverId,
          userName: driver.driverName || 'Без имени',
          userEmail: driver.driverEmail || '—',
          phone: driver.driverPhone || '—',
          defaultRate: stored?.defaultRate ?? 100,
          totalDeliveries: driver.totalDeliveries ?? 0,
          avgRating: driver.avgRating ?? 0,
          ratingCount: driver.ratingCount ?? 0,
          totalEarnings: stored?.totalEarnings ?? 0,
          status: stored?.status ?? 'active',
          routes: stored?.routes ?? [],
          deliveries: driver.deliveries || []
        };
      });

      setDrivers(mergedDrivers);
      localStorage.setItem('my_delivery_drivers', JSON.stringify(mergedDrivers));
    } catch (error) {
      console.error('Не удалось загрузить доставщиков из API', error);
      loadLocalDrivers();
    } finally {
      setLoading(false);
    }
  }, [loadLocalDrivers, user]);

  React.useEffect(() => {
    loadDriversFromApi();
  }, [loadDriversFromApi]);

  const formatCurrency = (value: number) =>
    value.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

  const openSalaryModal = (driver: DeliveryDriver) => {
    setSalaryDriver(driver);
    setSalaryResult(null);
    setSalaryModalVisible(true);
    const now = dayjs();
    salaryForm.setFieldsValue({
      period: now,
      deliveries: driver.totalDeliveries,
      baseRate: driver.defaultRate,
      bonus: 0,
      penalty: 0,
      routes: driver.routes.map((route) => ({
        routeId: route.id,
        fromAddress: route.fromAddress,
        toAddress: route.toAddress,
        rate: route.customRate,
        deliveries: 0
      }))
    });
  };

  const openRateModal = (driver: DeliveryDriver) => {
    if (driver.deliveries.length === 0) {
      message.info('Для этого доставщика пока нет доставок для оценки');
      return;
    }
    setRateDriverState(driver);
    const firstNotRated =
      driver.deliveries.find((delivery) => !delivery.existingRating) || driver.deliveries[0];
    rateForm.setFieldsValue({
      orderId: firstNotRated?.orderId,
      rating: firstNotRated?.existingRating?.rating ?? 5,
      comment: firstNotRated?.existingRating?.comment ?? ''
    });
    setRateModalVisible(true);
  };

  const handleRateOrderChange = (orderId: number) => {
    if (!rateDriverState) return;
    const delivery = rateDriverState.deliveries.find((d) => d.orderId === orderId);
    if (!delivery) return;
    rateForm.setFieldsValue({
      rating: delivery.existingRating?.rating ?? 5,
      comment: delivery.existingRating?.comment ?? ''
    });
  };

  const handleSubmitRating = async () => {
    try {
      const values = await rateForm.validateFields();
      setRateLoading(true);
      await deliveryRatingsAPI.rateDriver({
        orderId: Number(values.orderId),
        rating: Number(values.rating),
        comment: values.comment
      });
      message.success('Оценка сохранена');
      setRateModalVisible(false);
      setRateDriverState(null);
      rateForm.resetFields();
      loadDriversFromApi();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Не удалось сохранить оценку');
    } finally {
      setRateLoading(false);
    }
  };

  const handleCalculateSalary = async () => {
    if (!salaryDriver) return;
    try {
      setSalaryLoading(true);
      const values = await salaryForm.validateFields();

      const period: Dayjs = values.period;
      const deliveries: number = Number(values.deliveries) || 0;
      const baseRate: number = Number(values.baseRate) || salaryDriver.defaultRate;
      const bonus: number = Number(values.bonus) || 0;
      const penalty: number = Number(values.penalty) || 0;

      const basePay = deliveries * baseRate;

      const routes = values.routes || [];
      const routePay = routes.reduce((sum: number, route: any) => {
        const count = Number(route.deliveries) || 0;
        const rate = Number(route.rate) || 0;
        return sum + count * rate;
      }, 0);

      // Если по маршрутам ничего не введено, используем базовую оплату
      const effectiveRoutePay = routePay > 0 ? routePay : 0;
      const finalPay = (routePay > 0 ? routePay : basePay) + bonus - penalty;

      setSalaryResult({
        periodLabel: period.format('MMMM YYYY'),
        basePay,
        routePay: effectiveRoutePay,
        bonus,
        penalty,
        totalDeliveries: deliveries,
        finalPay
      });
    } catch (error) {
      // ignore, form validation handles messages
    } finally {
      setSalaryLoading(false);
    }
  };

  const handleAddRoute = async () => {
    try {
      const values = await routeForm.validateFields();
      if (!selectedDriver) return;

      const newRoute: DeliveryRoute = {
        id: Math.max(...(selectedDriver.routes.map(r => r.id) || [0]), 0) + 1,
        fromAddress: values.fromAddress,
        toAddress: values.toAddress,
        distance: parseFloat(values.distance),
        estimatedTime: parseInt(values.estimatedTime, 10),
        autoRate: parseFloat(values.customRate) || 100,
        customRate: parseFloat(values.customRate)
      };

      const updatedDrivers = drivers.map(d =>
        d.id === selectedDriver.id
          ? { ...d, routes: [...d.routes, newRoute] }
          : d
      );

      setDrivers(updatedDrivers);
      localStorage.setItem('my_delivery_drivers', JSON.stringify(updatedDrivers));
      
      message.success(`Маршрут добавлен (${newRoute.distance} км → ${newRoute.customRate}₽)`);
      setAddRouteVisible(false);
      routeForm.resetFields();
      
      // Обновляем selectedDriver
      setSelectedDriver(updatedDrivers.find(d => d.id === selectedDriver.id) || null);
    } catch (error) {
      message.error('Ошибка добавления маршрута');
    }
  };

  const handleDeleteRoute = (routeId: number) => {
    if (!selectedDriver) return;
    
    Modal.confirm({
      title: 'Удалить маршрут?',
      content: 'Это действие нельзя отменить',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk() {
        const updatedDrivers = drivers.map(d =>
          d.id === selectedDriver.id
            ? { ...d, routes: d.routes.filter(r => r.id !== routeId) }
            : d
        );
        setDrivers(updatedDrivers);
        localStorage.setItem('my_delivery_drivers', JSON.stringify(updatedDrivers));
        setSelectedDriver(updatedDrivers.find(d => d.id === selectedDriver.id) || null);
        message.success('Маршрут удален');
      }
    });
  };

  const columns = [
    {
      title: 'Доставщик',
      key: 'driver',
      width: 200,
      render: (_: any, record: DeliveryDriver) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.userName}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.userEmail}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.phone}</div>
        </div>
      )
    },
    {
      title: 'Базовая ставка',
      dataIndex: 'defaultRate',
      key: 'defaultRate',
      width: 120,
      render: (rate: number) => (
        <span style={{ fontSize: '14px', fontWeight: 600 }}>{rate}₽</span>
      )
    },
    {
      title: 'Маршрутов',
      key: 'routes',
      width: 100,
      render: (_: any, record: DeliveryDriver) => (
        <Tag color="blue">{record.routes.length}</Tag>
      )
    },
    {
      title: 'Доставок',
      dataIndex: 'totalDeliveries',
      key: 'totalDeliveries',
      width: 100,
      sorter: (a: DeliveryDriver, b: DeliveryDriver) => b.totalDeliveries - a.totalDeliveries
    },
    {
      title: 'Рейтинг',
      dataIndex: 'avgRating',
      key: 'avgRating',
      width: 100,
      render: (rating: number, record: DeliveryDriver) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: rating > 4.5 ? '#52c41a' : '#faad14'
            }}
          >
            {rating.toFixed(1)}/5
          </span>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.ratingCount} оценок
          </Text>
        </div>
      )
    },
    {
      title: 'Заработок (месяц)',
      dataIndex: 'totalEarnings',
      key: 'totalEarnings',
      width: 150,
      render: (earnings: number) => (
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#52c41a' }}>
          {earnings.toLocaleString('ru')}₽
        </span>
      ),
      sorter: (a: DeliveryDriver, b: DeliveryDriver) => b.totalEarnings - a.totalEarnings
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 360,
      fixed: 'right' as const,
      render: (_: any, record: DeliveryDriver) => (
        <Space size="small">
          {user?.role === 'supplier' ? (
            <Button
              size="small"
              onClick={() => {
                setEditingDriver(record);
                editDriverForm.setFieldsValue({
                  fullName: record.userName || '',
                  email: record.userEmail || '',
                  phone: record.phone || ''
                });
                setEditDriverVisible(true);
              }}
            >
              Редактировать
            </Button>
          ) : null}
          {user?.role === 'supplier' ? (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Удалить доставщика?',
                  content:
                    'Доставщик будет отключен и исчезнет из списка. История доставок и заказов сохранится.',
                  okText: 'Удалить',
                  cancelText: 'Отмена',
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    try {
                      await supplierDeliveryTeamAPI.deleteDriver(record.userId);
                    } catch (e: any) {
                      // Even if API fails, allow removing from local list
                    }
                    const updated = drivers.filter((d) => d.userId !== record.userId);
                    setDrivers(updated);
                    localStorage.setItem('my_delivery_drivers', JSON.stringify(updated));
                    message.success('Доставщик удалён');
                  }
                });
              }}
            >
              Удалить
            </Button>
          ) : null}
          <Button
            size="small"
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setSelectedDriver(record);
              setRoutesDrawerVisible(true);
            }}
          >
            Маршруты
          </Button>
          <Button
            size="small"
            icon={<StarOutlined />}
            onClick={() => openRateModal(record)}
            disabled={record.deliveries.length === 0}
          >
            Оценить
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => openSalaryModal(record)}
          >
            Расчет
          </Button>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => message.info('Настройки ставок - в разработке')}
          >
            Настройки
          </Button>
        </Space>
      )
    }
  ];

  const totalDrivers = drivers.length;
  const totalDeliveries = drivers.reduce((sum, d) => sum + d.totalDeliveries, 0);
  const totalEarnings = drivers.reduce((sum, d) => sum + d.totalEarnings, 0);
  const aggregateRatingWeight = drivers.reduce(
    (acc, driver) => acc + driver.avgRating * (driver.ratingCount || 0),
    0
  );
  const aggregateRatingCount = drivers.reduce((acc, driver) => acc + (driver.ratingCount || 0), 0);
  const avgRating =
    aggregateRatingCount > 0 ? aggregateRatingWeight / aggregateRatingCount : 0;

  return (
    <>
    
      <Card title={<><CarOutlined /> Мои доставщики</>}>
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Всего доставщиков"
              value={totalDrivers}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Доставок (месяц)"
              value={totalDeliveries}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Общая зарплата"
              value={totalEarnings}
              suffix="₽"
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Ср. рейтинг"
              value={avgRating.toFixed(1)}
              suffix="/5"
              valueStyle={{ color: avgRating > 4.5 ? '#52c41a' : '#faad14' }}
            />
          </Col>
        </Row>

        <Space style={{ marginBottom: '16px' }} wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
            Добавить доставщика
          </Button>
          {canReviewRegistrations && (
            <Badge count={pendingRegistrationsCount} size="small" offset={[-4, 4]}>
              <Button
                icon={<UserAddOutlined />}
                onClick={() => setRegistrationsDrawerOpen(true)}
              >
                Заявки на регистрацию
              </Button>
            </Badge>
          )}
        </Space>

        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={drivers}
          pagination={false}
          locale={{ emptyText: 'Нет доставщиков. Добавьте первого!' }}
          scroll={isMobile ? { x: 'max-content' } : { x: 1100, y: 600 }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      <Modal
        title={editingDriver ? `Редактирование: ${editingDriver.userName}` : 'Редактирование доставщика'}
        open={editDriverVisible}
        onCancel={() => {
          setEditDriverVisible(false);
          setEditingDriver(null);
          editDriverForm.resetFields();
        }}
        onOk={async () => {
          if (!editingDriver) return;
          try {
            const values = await editDriverForm.validateFields();
            setEditDriverSaving(true);
            await supplierDeliveryTeamAPI.updateDriver(editingDriver.userId, {
              fullName: values.fullName,
              email: values.email,
              phone: values.phone
            });
            message.success('Данные доставщика обновлены');
            setEditDriverVisible(false);
            setEditingDriver(null);
            editDriverForm.resetFields();
            loadDriversFromApi();
          } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.response?.data?.error || 'Не удалось обновить доставщика');
          } finally {
            setEditDriverSaving(false);
          }
        }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={editDriverSaving}
      >
        <Form form={editDriverForm} layout="vertical">
          <Form.Item
            label="ФИО"
            name="fullName"
            rules={[{ required: true, message: 'Введите ФИО доставщика' }]}
          >
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="driver@example.com" />
          </Form.Item>
          <Form.Item label="Телефон" name="phone">
            <Input placeholder="+7 (999) 123-45-67" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Заявки на регистрацию"
        placement="right"
        width={
          typeof window !== 'undefined'
            ? Math.min(window.innerWidth - 24, isMobile ? window.innerWidth : 1100)
            : 1100
        }
        open={registrationsDrawerOpen}
        onClose={() => {
          setRegistrationsDrawerOpen(false);
          loadPendingRegistrationsCount();
        }}
        destroyOnClose
      >
        {registrationsDrawerOpen ? <AdminApprovals embedded /> : null}
      </Drawer>

      {/* Drawer для управления маршрутами */}
      <Drawer
        title={`Маршруты: ${selectedDriver?.userName}`}
        placement="right"
        onClose={() => {
          setRoutesDrawerVisible(false);
          setSelectedDriver(null);
        }}
        open={routesDrawerVisible}
        width={isMobile ? '100%' : 700}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddRouteVisible(true)}>
            Добавить маршрут
          </Button>
        }
      >
        {selectedDriver && (
          <div>
            <div style={{ marginBottom: '16px', padding: '12px', background: '#fafafa', borderRadius: '6px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#999' }}>Базовая ставка:</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>{selectedDriver.defaultRate}₽</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#999' }}>Сохранено маршрутов:</div>
                  <div style={{ fontSize: '18px', fontWeight: 600 }}>{selectedDriver.routes.length}</div>
                </Col>
              </Row>
            </div>

            {selectedDriver.routes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedDriver.routes.map((route) => (
                  <div
                    key={route.id}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '8px',
                      padding: '12px',
                      background: '#fff'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                          <EnvironmentOutlined /> Маршрут
                        </div>
                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                          📍 {route.fromAddress}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                          → {route.toAddress}
                        </div>
                        <Space size="small">
                          <Tag>📏 {route.distance} км</Tag>
                          <Tag>⏱ ~{route.estimatedTime} мин</Tag>
                          <Tag color={route.customRate !== route.autoRate ? 'orange' : 'blue'}>
                            {route.customRate}₽
                          </Tag>
                          {route.customRate !== route.autoRate && (
                            <Tag color="default">авто: {route.autoRate}₽</Tag>
                          )}
                        </Space>
                      </div>
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteRoute(route.id)}
                      >
                        Удалить
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <EnvironmentOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>Маршруты не настроены</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  Будет использоваться базовая ставка {selectedDriver.defaultRate}₽
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Модаль добавления маршрута */}
      <Modal
        title="Добавить новый маршрут"
        open={addRouteVisible}
        onCancel={() => {
          setAddRouteVisible(false);
          routeForm.resetFields();
        }}
        onOk={handleAddRoute}
        okText="Сохранить маршрут"
        cancelText="Отмена"
        width={isMobile ? '100%' : 800}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        <Form form={routeForm} layout="vertical">
          <Form.Item
            label="Откуда (ваш адрес/склад)"
            name="fromAddress"
            rules={[{ required: true, message: 'Введите адрес отправления' }]}
          >
            <Input placeholder="Москва, ул. Ленина, 1" size="large" />
          </Form.Item>

          <Form.Item
            label="Куда (адрес доставки)"
            name="toAddress"
            rules={[{ required: true, message: 'Введите адрес доставки' }]}
          >
            <Input placeholder="Москва, пр. Мира, 10" size="large" />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Расчёт по картам позже"
            description="Авторасчёт расстояния и времени через карты подключим при масштабировании. Сейчас укажите расстояние, время в пути и ставку вручную."
          />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Расстояние (км)"
                name="distance"
                rules={[{ required: true, message: 'Укажите расстояние' }]}
              >
                <InputNumber min={0.1} step={0.1} size="large" style={{ width: '100%' }} placeholder="12.5" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Время (мин)"
                name="estimatedTime"
                rules={[{ required: true, message: 'Укажите время' }]}
              >
                <InputNumber min={1} step={1} size="large" style={{ width: '100%' }} placeholder="35" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Ваша ставка (₽)"
                name="customRate"
                rules={[{ required: true, message: 'Укажите ставку' }]}
              >
                <InputNumber
                  min={50}
                  max={1000}
                  size="large"
                  style={{ width: '100%' }}
                  placeholder="100"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Модаль расчета зарплаты */}
      <Modal
        title={salaryDriver ? `Расчет зарплаты: ${salaryDriver.userName}` : 'Расчет зарплаты'}
        open={salaryModalVisible}
        onCancel={() => {
          setSalaryModalVisible(false);
          setSalaryDriver(null);
          setSalaryResult(null);
          salaryForm.resetFields();
        }}
        onOk={handleCalculateSalary}
        okText="Рассчитать"
        cancelText="Закрыть"
        width={isMobile ? '100%' : 720}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        confirmLoading={salaryLoading}
      >
        {salaryDriver && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form form={salaryForm} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Период"
                    name="period"
                    rules={[{ required: true, message: 'Укажите месяц' }]}
                  >
                    <DatePicker picker="month" style={{ width: '100%' }} format="MMMM YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Количество доставок"
                    name="deliveries"
                    rules={[{ required: true, message: 'Укажите количество доставок' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="Например, 60" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Базовая ставка, ₽"
                    name="baseRate"
                    rules={[{ required: true, message: 'Укажите базовую ставку' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="Ставка за доставку" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Бонусы, ₽" name="bonus">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Штрафы, ₽" name="penalty">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">
                <CalculatorOutlined /> Оплата по маршрутам (по желанию)
              </Divider>

              <Form.List name="routes">
                {(fields) => (
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {fields.length === 0 && (
                      <Text type="secondary">
                        Если хотите учитывать оплату по маршрутам — укажите, сколько доставок было сделано по каждому.
                        Если оставить пустым, зарплата посчитается по базовой ставке.
                      </Text>
                    )}
                    {fields.map((field, index) => {
                      const route = salaryDriver.routes[index];
                      if (!route) return null;
                      return (
                        <Card
                          key={field.key}
                          size="small"
                          title={`${route.fromAddress} → ${route.toAddress}`}
                          bordered
                        >
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                {...field}
                                label="Кол-во доставок"
                                name={[field.name, 'deliveries']}
                                fieldKey={[field.fieldKey!, 'deliveries']}
                              >
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                {...field}
                                label="Ставка за маршрут, ₽"
                                name={[field.name, 'rate']}
                                fieldKey={[field.fieldKey!, 'rate']}
                                rules={[{ required: true, message: 'Укажите ставку' }]}
                              >
                                <InputNumber min={0} style={{ width: '100%' }} placeholder={route.customRate.toString()} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      );
                    })}
                  </Space>
                )}
              </Form.List>
            </Form>

            {salaryResult && (
              <Card
                size="small"
                title={`Итог за ${salaryResult.periodLabel}`}
                headStyle={{ background: '#f6ffed', borderBottom: '1px solid #b7eb8f' }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={6}>
                    <Statistic title="Доставок" value={salaryResult.totalDeliveries} />
                  </Col>
                  <Col xs={24} md={6}>
                    <Statistic
                      title="По базовой ставке"
                      value={salaryResult.basePay}
                      prefix="₽"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={24} md={6}>
                    <Statistic
                      title="По маршрутам"
                      value={salaryResult.routePay}
                      prefix="₽"
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col xs={24} md={6}>
                    <Statistic
                      title="Бонусы/штрафы"
                      value={salaryResult.bonus - salaryResult.penalty}
                      prefix="₽"
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                </Row>
                <Divider />
                <Row>
                  <Col span={24}>
                    <Statistic
                      title="Итого к выплате"
                      value={salaryResult.finalPay}
                      prefix="₽"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Модаль оценки доставщика */}
      <Modal
        title={
          rateDriverState ? `Оценить доставщика: ${rateDriverState.userName}` : 'Оценить доставщика'
        }
        open={rateModalVisible}
        onCancel={() => {
          setRateModalVisible(false);
          setRateDriverState(null);
          rateForm.resetFields();
        }}
        onOk={handleSubmitRating}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={rateLoading}
        width={520}
      >
        {rateDriverState ? (
          <Form form={rateForm} layout="vertical">
            <Form.Item
              label="Доставка"
              name="orderId"
              rules={[{ required: true, message: 'Выберите заказ' }]}
            >
              <Select
                placeholder="Выберите заказ для оценки"
                onChange={handleRateOrderChange}
                options={rateDriverState.deliveries.map((delivery) => ({
                  value: delivery.orderId,
                  label: `Заказ #${delivery.orderId} • ${dayjs(delivery.deliveredAt).format(
                    'DD.MM.YYYY HH:mm'
                  )}`
                }))}
              />
            </Form.Item>

            {(() => {
              const selectedOrderId = rateForm.getFieldValue('orderId');
              const currentDelivery = rateDriverState.deliveries.find(
                (delivery) => delivery.orderId === selectedOrderId
              );

              return (
                currentDelivery && (
                  <Space direction="vertical" size="small" style={{ marginBottom: 12 }}>
                    {currentDelivery.customerCompany && (
                      <Text type="secondary">
                        Заказчик: {currentDelivery.customerCompany}
                      </Text>
                    )}
                    {currentDelivery.supplierCompany && (
                      <Text type="secondary">
                        Поставщик: {currentDelivery.supplierCompany}
                      </Text>
                    )}
                    {currentDelivery.existingRating && (
                      <Text type="warning">
                        Вы уже оценили эту доставку на {currentDelivery.existingRating.rating}★. При
                        необходимости вы можете обновить оценку.
                      </Text>
                    )}
                  </Space>
                )
              );
            })()}

            <Form.Item
              label="Оценка"
              name="rating"
              rules={[{ required: true, message: 'Укажите оценку' }]}
            >
              <Rate allowClear={false} />
            </Form.Item>

            <Form.Item label="Комментарий" name="comment">
              <Input.TextArea rows={3} placeholder="Поделитесь впечатлением о работе доставщика" />
            </Form.Item>
          </Form>
        ) : (
          <Text type="secondary">Выберите доставщика для оценки.</Text>
        )}
      </Modal>
    </>
  );
};

export default MyDeliveryTeam;

